import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Plus, Calendar, AlertCircle, Edit, Trash2, Settings, CheckCircle2 } from 'lucide-react';
import AssessmentFiles from '../components/AssessmentFiles';
import AccidentFiles from '../components/AccidentFiles';
import { format } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function DriverDetail() {
  const { driverId } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [accidents, setAccidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAssessmentDialogOpen, setIsAssessmentDialogOpen] = useState(false);
  const [isAccidentDialogOpen, setIsAccidentDialogOpen] = useState(false);
  const [isEditAccidentDialogOpen, setIsEditAccidentDialogOpen] = useState(false);
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [isEditDriverDialogOpen, setIsEditDriverDialogOpen] = useState(false);
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const [isEditCompletedDialogOpen, setIsEditCompletedDialogOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [selectedAccident, setSelectedAccident] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [completionResult, setCompletionResult] = useState('passed');
  const [completionNotes, setCompletionNotes] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [editDriverData, setEditDriverData] = useState({ name: '', employee_id: '', hire_date: '', status: 'active', shift: '', employment_type: '' });
  const [editAccidentData, setEditAccidentData] = useState({ date: '', description: '', severity: 'minor', blameworthy: '' });
  const [newAssessment, setNewAssessment] = useState({
    assessment_type: 'Yearly Peer-to-Peer Review',
    course_title: '',
    scheduled_date: '',
    notes: '',
    assessor: ''
  });
  const [newAccident, setNewAccident] = useState({
    date: '',
    description: '',
    severity: 'minor'
  });

  const [assessmentFilter, setAssessmentFilter] = useState(() => {
    return localStorage.getItem('driver_detail_assessment_filter') || 'all';
  });


  useEffect(() => {
    fetchCurrentUser();
    fetchDriverData();
  }, [driverId]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, getAuthHeaders());
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user');
    }
  };

  async function fetchDriverData() {
    try {
      const [driverRes, assessmentsRes, accidentsRes] = await Promise.all([
        axios.get(`${API}/drivers/${driverId}`, getAuthHeaders()),
        axios.get(`${API}/assessments?driver_id=${driverId}`, getAuthHeaders()),
        axios.get(`${API}/accidents?driver_id=${driverId}`, getAuthHeaders())
      ]);
      setDriver(driverRes.data);
      setAssessments(assessmentsRes.data);
      setAccidents(accidentsRes.data);
    } catch (error) {
      toast.error('Failed to load driver data');
    } finally {
      setLoading(false);
    }
  }

  const handleAddAssessment = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        driver_id: driverId,
        ...newAssessment,
        scheduled_date: new Date(newAssessment.scheduled_date).toISOString()
      };
      await axios.post(`${API}/assessments`, payload, getAuthHeaders());
      toast.success('Assessment scheduled successfully');
      
      // Close dialog and reset form
      setIsAssessmentDialogOpen(false);
      setTimeout(() => {
        setNewAssessment({
          assessment_type: 'Yearly Peer-to-Peer Review',
          course_title: '',
          scheduled_date: '',
          notes: '',
          assessor: ''
        });
      }, 300);
      
      fetchDriverData();
    } catch (error) {
      toast.error('Failed to schedule assessment');
    }
  };

  const handleAddAccident = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        driver_id: driverId,
        ...newAccident,
        date: new Date(newAccident.date).toISOString()
      };
      await axios.post(`${API}/accidents`, payload, getAuthHeaders());
      toast.success('Accident record added successfully');
      
      // Close dialog and reset form
      setIsAccidentDialogOpen(false);
      setTimeout(() => {
        setNewAccident({ date: '', description: '', severity: 'minor' });
      }, 300);
      
      fetchDriverData();
    } catch (error) {
      toast.error('Failed to add accident record');
    }
  };

  const handleOpenEditAccident = (accident) => {
    setSelectedAccident(accident);
    setEditAccidentData({
      date: format(new Date(accident.date), "yyyy-MM-dd'T'HH:mm"),
      description: accident.description,
      severity: accident.severity || 'minor',
      blameworthy: accident.blameworthy || ''
    });
    setIsEditAccidentDialogOpen(true);
  };

  const handleEditAccident = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...editAccidentData,
        date: new Date(editAccidentData.date).toISOString()
      };
      await axios.put(`${API}/accidents/${selectedAccident.id}`, payload, getAuthHeaders());
      toast.success('Accident record updated successfully');
      setIsEditAccidentDialogOpen(false);
      setSelectedAccident(null);
      fetchDriverData();
    } catch (error) {
      toast.error('Failed to update accident record');
    }
  };

  const handleDeleteAccident = async (accidentId) => {
    if (!window.confirm('Delete this accident record? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/accidents/${accidentId}`, getAuthHeaders());
      toast.success('Accident record deleted successfully');
      fetchDriverData();
    } catch (error) {
      toast.error('Failed to delete accident record');
    }
  };

  const handleOpenCompleteAssessment = (assessment) => {
    setSelectedAssessment(assessment);
    // Default to today's date and passed result
    setCompletionDate(format(new Date(), 'yyyy-MM-dd'));
    setCompletionResult('passed');
    setCompletionNotes('');
    setIsCompleteDialogOpen(true);
  };

  const handleCompleteAssessment = async (e) => {
    e.preventDefault();
    
    try {
      // Update assessment status
      await axios.put(
        `${API}/assessments/${selectedAssessment.id}`,
        {
          status: 'completed',
          completed_date: new Date(completionDate).toISOString(),
          result: completionResult,
          completion_notes: completionNotes
        },
        getAuthHeaders()
      );
      
      // Upload PDF files if any
      if (selectedFiles.length > 0) {
        const formData = new FormData();
        for (let file of selectedFiles) {
          formData.append('files', file);
        }
        
        await axios.post(
          `${API}/assessments/${selectedAssessment.id}/upload`,
          formData,
          {
            headers: {
              ...getAuthHeaders().headers,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      
      toast.success(`Assessment marked as ${completionResult}`);
      setIsCompleteDialogOpen(false);
      setSelectedAssessment(null);
      setCompletionDate('');
      setCompletionResult('passed');
      setCompletionNotes('');
      setSelectedFiles([]);
      fetchDriverData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update assessment');
    }
  };

  const handleDeleteAssessment = async (assessmentId) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) return;
    
    try {
      await axios.delete(`${API}/assessments/${assessmentId}`, getAuthHeaders());
      toast.success('Assessment deleted successfully');
      fetchDriverData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete assessment');
    }
  };

  const handleOpenEditCompleted = (assessment) => {
    setSelectedAssessment(assessment);
    setCompletionResult(assessment.result || 'passed');
    setCompletionNotes(assessment.completion_notes || '');
    setIsEditCompletedDialogOpen(true);
  };

  const handleEditCompleted = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(
        `${API}/assessments/${selectedAssessment.id}`,
        {
          result: completionResult,
          completion_notes: completionNotes
        },
        getAuthHeaders()
      );
      toast.success('Assessment notes updated successfully');
      setIsEditCompletedDialogOpen(false);
      setSelectedAssessment(null);
      fetchDriverData();
    } catch (error) {
      toast.error('Failed to update assessment notes');
    }
  };

  const handleOpenReschedule = (assessment) => {
    setSelectedAssessment(assessment);
    setRescheduleDate(format(new Date(assessment.scheduled_date), 'yyyy-MM-dd'));
    setIsRescheduleDialogOpen(true);
  };

  const handleRescheduleAssessment = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(
        `${API}/assessments/${selectedAssessment.id}`,
        {
          scheduled_date: new Date(rescheduleDate).toISOString()
        },
        getAuthHeaders()
      );
      toast.success('Assessment rescheduled successfully');
      setIsRescheduleDialogOpen(false);
      setSelectedAssessment(null);
      setRescheduleDate('');
      fetchDriverData();
    } catch (error) {
      toast.error('Failed to reschedule assessment');
    }
  };

  const handleOpenEditDriver = () => {
    setEditDriverData({
      name: driver.name,
      employee_id: driver.employee_id || '',
      hire_date: driver.hire_date ? format(new Date(driver.hire_date), 'yyyy-MM-dd') : '',
      status: driver.status,
      shift: driver.shift || '',
      employment_type: driver.employment_type || '',
    });
    setIsEditDriverDialogOpen(true);
  };

  const handleEditDriver = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...editDriverData,
        hire_date: editDriverData.hire_date ? new Date(editDriverData.hire_date).toISOString() : null,
        shift: editDriverData.shift || null,
        employment_type: editDriverData.employment_type || null,
      };
      await axios.put(`${API}/drivers/${driverId}`, payload, getAuthHeaders());
      toast.success('Driver updated successfully');
      setIsEditDriverDialogOpen(false);
      fetchDriverData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update driver');
    }
  };

  const canManageAssessments = currentUser?.role === 'admin' || currentUser?.role === 'coach';
  const canEditDrivers = currentUser?.role === 'admin' || currentUser?.role === 'coach';
  const canManageAccidents = currentUser?.role === 'admin' || currentUser?.role === 'coach' || currentUser?.role === 'manager';

  const sortedAccidents = useMemo(() => {
    return [...accidents].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [accidents]);

  const latestAccident = useMemo(() => {
    return sortedAccidents.length > 0 ? sortedAccidents[0] : null;
  }, [sortedAccidents]);

  const getAssessmentSortDate = (a) => {
    if (a.status === 'completed' && a.completed_date) return new Date(a.completed_date);
    return new Date(a.scheduled_date);
  };

  const sortedAssessments = useMemo(() => {
    return [...assessments].sort((a, b) => getAssessmentSortDate(b) - getAssessmentSortDate(a));
  }, [assessments]);

  const assessmentFilterDefs = useMemo(
    () => [
      { key: 'all', label: 'All', match: () => true },
      {
        key: 'p2p',
        label: 'P2P',
        match: (a) => a.assessment_type === 'Yearly Peer-to-Peer Review',
      },
      {
        key: 'risk',
        label: 'Risk',
        match: (a) => a.assessment_type === 'Risk Assessment',
      },
      {
        key: 'induction',
        label: 'Induction',
        match: (a) => a.assessment_type === 'New Driver Induction',
      },
      {
        key: 'training',
        label: 'Training',
        match: (a) => a.assessment_type === 'Training Course',
      },
      {
        key: 'coaching',
        label: 'Coaching',
        match: (a) => a.assessment_type === 'Coaching Intervention' || a.assessment_type === 'Coaching Ride Along',
      },
    ],
    []
  );

  const assessmentCounts = useMemo(() => {
    const counts = {};
    for (const def of assessmentFilterDefs) {
      counts[def.key] = sortedAssessments.filter(def.match).length;
    }
    return counts;
  }, [assessmentFilterDefs, sortedAssessments]);

  const filteredAssessments = useMemo(() => {
    const def = assessmentFilterDefs.find((d) => d.key === assessmentFilter) || assessmentFilterDefs[0];
    return sortedAssessments.filter(def.match);
  }, [assessmentFilter, assessmentFilterDefs, sortedAssessments]);

  useEffect(() => {
    localStorage.setItem('driver_detail_assessment_filter', assessmentFilter);
  }, [assessmentFilter]);

  if (loading) {
    return <div className="text-center py-12">Loading driver details...</div>;
  }

  if (!driver) {
    return <div className="text-center py-12">Driver not found</div>;
  }

  return (
    <div className="space-y-8" data-testid="driver-detail-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate('/drivers')} data-testid="back-button">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-4xl font-bold text-slate-800 dark:text-slate-200">{driver.name}</h1>
            {driver.employee_id && (
              <p className="text-slate-600 dark:text-slate-400">Employee ID: {driver.employee_id}</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/drivers/${driverId}/driver-report`)}
            data-testid="driver-report-button"
          >
            Driver Report
          </Button>
          {canEditDrivers && (
            <Button variant="outline" onClick={handleOpenEditDriver} data-testid="edit-driver-button">
              <Settings className="h-4 w-4 mr-2" />
              Edit Driver
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card data-testid="driver-info-card">
          <CardHeader>
            <CardTitle>Driver Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Status</p>
              <Badge variant={driver.status === 'active' ? 'default' : 'secondary'}>
                {driver.status}
              </Badge>
            </div>
            {driver.hire_date && (
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Hire Date</p>
                <p className="text-slate-800 dark:text-slate-200">{format(new Date(driver.hire_date), 'MMMM d, yyyy')}</p>
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Total Assessments</p>
              <p className="text-slate-800 dark:text-slate-200">{assessments.length}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Accident Records</p>
              <p className="text-slate-800 dark:text-slate-200">{accidents.length}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Latest RTC</p>
              {latestAccident ? (
                <div className="space-y-1">
                  <p className="text-slate-800 dark:text-slate-200">
                    {format(new Date(latestAccident.date), 'MMM d, yyyy')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {latestAccident.severity && (
                      <Badge variant={latestAccident.severity === 'severe' ? 'destructive' : 'secondary'}>
                        {latestAccident.severity}
                      </Badge>
                    )}
                    {latestAccident.blameworthy && (
                      <Badge
                        variant={latestAccident.blameworthy === 'non-blameworthy' ? 'default' : 'destructive'}
                        className={latestAccident.blameworthy === 'non-blameworthy' ? 'bg-green-600' : 'bg-red-600'}
                      >
                        {latestAccident.blameworthy === 'non-blameworthy' ? 'Non-Blameworthy' : 'Blameworthy'}
                      </Badge>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 dark:text-slate-400">—</p>
              )}
            </div>
          </CardContent>
        </Card>

        {(() => {
          const p2pAssessments = assessments.filter(a => a.assessment_type === 'Yearly Peer-to-Peer Review');
          const completedP2P = p2pAssessments.filter(a => a.status === 'completed').sort((a, b) => 
            new Date(b.completed_date) - new Date(a.completed_date)
          );
          const scheduledP2P = p2pAssessments.find(a => a.status === 'scheduled' || a.status === 'overdue' || a.status === 'due');
          const lastCompleted = completedP2P[0];
          
          const now = new Date();
          const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          
          let statusText = '';
          let statusVariant = 'destructive';
          let statusBorderColor = 'border-slate-300';
          let statusBgColor = 'bg-slate-50 dark:bg-slate-800/30';
          let daysToNext = null;
          let isCompliant = false;
          const twoYearsAgo = new Date(now.getTime() - 730 * 24 * 60 * 60 * 1000);
          
          if (lastCompleted) {
            const completedDate = new Date(lastCompleted.completed_date);
            const daysSinceCompletion = Math.floor((now - completedDate) / (1000 * 60 * 60 * 24));
            
            if (completedDate > oneYearAgo) {
              // Completed within last year - compliant
              isCompliant = true;
              statusText = 'Compliant';
              statusVariant = 'secondary';
              statusBorderColor = 'border-blue-200';
              statusBgColor = 'bg-blue-50 dark:bg-blue-900/20/30';
              daysToNext = 365 - daysSinceCompletion;
            } else if (completedDate > twoYearsAgo) {
              // 1-2 years since completion - DUE (amber warning)
              statusText = 'Due';
              statusVariant = 'secondary';
              statusBorderColor = 'border-amber-200';
              statusBgColor = 'bg-amber-50 dark:bg-amber-900/20/30';
              if (scheduledP2P) {
                const scheduledDate = new Date(scheduledP2P.scheduled_date);
                daysToNext = Math.floor((scheduledDate - now) / (1000 * 60 * 60 * 24));
              }
            } else {
              // Over 2 years since completion - OVERDUE (red critical)
              statusText = 'Overdue';
              statusVariant = 'destructive';
              statusBorderColor = 'border-red-200';
              statusBgColor = 'bg-red-50 dark:bg-red-900/20/30';
              if (scheduledP2P) {
                const scheduledDate = new Date(scheduledP2P.scheduled_date);
                daysToNext = Math.floor((scheduledDate - now) / (1000 * 60 * 60 * 24));
              }
            }
          } else {
            // Never completed P2P - OVERDUE (red critical)
            statusText = 'Overdue - No Completion';
            statusVariant = 'destructive';
            statusBorderColor = 'border-red-200';
            statusBgColor = 'bg-red-50 dark:bg-red-900/20/30';
            if (scheduledP2P) {
              const scheduledDate = new Date(scheduledP2P.scheduled_date);
              daysToNext = Math.floor((scheduledDate - now) / (1000 * 60 * 60 * 24));
            }
          }
          
          return (
            <Card data-testid="p2p-status-card" className={`lg:col-span-2 ${statusBorderColor} ${statusBgColor}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Yearly Peer-to-Peer Status</span>
                  <Badge variant={statusVariant}>{statusText}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {lastCompleted ? (
                  <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Last Completed P2P</p>
                    <p className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                      {format(new Date(lastCompleted.completed_date), 'MMMM d, yyyy')}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {Math.floor((now - new Date(lastCompleted.completed_date)) / (1000 * 60 * 60 * 24))} days ago
                    </p>
                  </div>
                ) : (
                  <div className="p-3 bg-slate-100 dark:bg-slate-700 rounded-lg border dark:border-slate-700 border-slate-300">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">⚠️ No P2P Completion on Record</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">This driver has never completed a peer-to-peer review</p>
                  </div>
                )}
                
                {scheduledP2P ? (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border dark:border-slate-700 border-blue-200">
                    <p className="text-sm font-medium text-blue-700">Next Scheduled P2P</p>
                    <p className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                      {format(new Date(scheduledP2P.scheduled_date), 'MMMM d, yyyy')}
                    </p>
                    {(() => {
                      const scheduledDate = new Date(scheduledP2P.scheduled_date);
                      const daysToScheduled = Math.floor((scheduledDate - now) / (1000 * 60 * 60 * 24));
                      return (
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {daysToScheduled > 0 ? `In ${daysToScheduled} days` : `${Math.abs(daysToScheduled)} days ago`}
                        </p>
                      );
                    })()}
                    {scheduledP2P.assessor && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Assessor: {scheduledP2P.assessor}</p>
                    )}
                  </div>
                ) : (
                  <div className="p-3 bg-slate-100 dark:bg-slate-700 rounded-lg border dark:border-slate-700 border-slate-300">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">📅 No P2P Scheduled</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Please schedule a peer-to-peer review</p>
                  </div>
                )}
                
                {isCompliant && lastCompleted && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border dark:border-slate-700 border-blue-200">
                    <p className="text-sm font-medium text-blue-700">✓ Next P2P Due By</p>
                    {(() => {
                      const completedDate = new Date(lastCompleted.completed_date);
                      // P2P due by 2 years from last completion (drivers have year 1-2 to complete)
                      const dueByDate = new Date(completedDate.getTime() + 730 * 24 * 60 * 60 * 1000);
                      const daysToDueBy = Math.floor((dueByDate - now) / (1000 * 60 * 60 * 24));
                      return (
                        <>
                          <p className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                            {format(dueByDate, 'MMMM d, yyyy')}
                          </p>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            ({daysToDueBy} days remaining in compliance window)
                          </p>
                        </>
                      );
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}
      </div>

      <Card data-testid="accidents-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Accident History</CardTitle>
          {canManageAccidents && (
            <Dialog open={isAccidentDialogOpen} onOpenChange={setIsAccidentDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="add-accident-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Accident Record
                </Button>
              </DialogTrigger>
            <DialogContent data-testid="add-accident-dialog">
              <DialogHeader>
                <DialogTitle>Add Accident Record</DialogTitle>
                <DialogDescription>
                  Record an accident incident for this driver.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddAccident} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="accident_date">Date & Time *</Label>
                  <Input
                    id="accident_date"
                    data-testid="accident-date-input"
                    type="datetime-local"
                    value={newAccident.date}
                    onChange={(e) => setNewAccident({ ...newAccident, date: e.target.value })}
                    required
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">Select date and time of incident</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="severity">Severity</Label>
                  <select
                    id="severity"
                    data-testid="accident-severity-select"
                    className="w-full px-3 py-2 border dark:border-slate-700 rounded-md"
                    value={newAccident.severity}
                    onChange={(e) => setNewAccident({ ...newAccident, severity: e.target.value })}
                  >
                    <option value="minor">Minor</option>
                    <option value="moderate">Moderate</option>
                    <option value="severe">Severe</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    data-testid="accident-description-input"
                    value={newAccident.description}
                    onChange={(e) => setNewAccident({ ...newAccident, description: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-accident-button">
                  Add Record
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {accidents.length === 0 ? (
            <p className="text-slate-500 dark:text-slate-400 text-sm">No accident records</p>
          ) : (
            <div className="space-y-3">
              {sortedAccidents.map((accident) => (
                <div key={accident.id} className="p-4 border dark:border-slate-700 rounded-lg" data-testid={`accident-${accident.id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2 text-orange-600" />
                      <p className="font-medium text-slate-800 dark:text-slate-200">{format(new Date(accident.date), 'MMMM d, yyyy')}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {accident.blameworthy && (
                        <Badge 
                          variant={accident.blameworthy === 'non-blameworthy' ? 'default' : 'destructive'}
                          className={accident.blameworthy === 'non-blameworthy' ? 'bg-green-600' : 'bg-red-600'}
                        >
                          {accident.blameworthy === 'non-blameworthy' ? 'Non-Blameworthy' : 'Blameworthy'}
                        </Badge>
                      )}
                      {accident.severity && (
                        <Badge variant={accident.severity === 'severe' ? 'destructive' : 'secondary'}>
                          {accident.severity}
                        </Badge>
                      )}
                      {canManageAccidents && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOpenEditAccident(accident)}
                          data-testid={`edit-accident-${accident.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {currentUser?.role === 'admin' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteAccident(accident.id)}
                          data-testid={`delete-accident-${accident.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{accident.description}</p>
                  
                  {currentUser && (
                    <AccidentFiles 
                      accident={accident} 
                      onUpdate={fetchDriverData}
                      canManage={canManageAccidents}
                      currentUserRole={currentUser.role}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

            <div className="flex flex-wrap gap-2 mb-4" data-testid="assessment-filters">
              {assessmentFilterDefs.map((f) => (
                <Button
                  key={f.key}
                  type="button"
                  size="sm"
                  variant={assessmentFilter === f.key ? 'default' : 'outline'}
                  onClick={() => setAssessmentFilter(f.key)}
                  data-testid={`assessment-filter-${f.key}`}
                >
                  {f.label} ({assessmentCounts[f.key] ?? 0})
                </Button>
              ))}
            </div>


      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 mt-6">
        <Card className="lg:col-span-2" data-testid="assessments-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Assessments</CardTitle>
            {canManageAssessments && (
              <Dialog open={isAssessmentDialogOpen} onOpenChange={setIsAssessmentDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="add-assessment-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Schedule Assessment
                  </Button>
                </DialogTrigger>
              <DialogContent data-testid="add-assessment-dialog">
                <DialogHeader>
                  <DialogTitle>Schedule Assessment</DialogTitle>
                  <DialogDescription>
                    Schedule a new assessment for this driver. Select the assessment type and date.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddAssessment} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="assessment_type">Assessment Type *</Label>
                    <select
                      id="assessment_type"
                      data-testid="assessment-type-select"
                      className="w-full px-3 py-2 border dark:border-slate-700 rounded-md"
                      value={newAssessment.assessment_type}
                      onChange={(e) => setNewAssessment({ ...newAssessment, assessment_type: e.target.value })}
                    >
                      <option value="Yearly Peer-to-Peer Review">Yearly Peer-to-Peer Review</option>
                      <option value="Risk Assessment">Risk Assessment</option>
                      <option value="New Driver Induction">New Driver Induction</option>
                      <option value="Training Course">Training Course</option>
                      <option value="Coaching Intervention">Coaching Intervention</option>
                      <option value="Coaching Ride Along">Coaching Ride Along</option>
                    </select>
                  </div>
                  {newAssessment.assessment_type === 'Training Course' && (
                    <div className="space-y-2">
                      <Label htmlFor="course_title">Course Title *</Label>
                      <Input
                        id="course_title"
                        data-testid="course-title-input"
                        value={newAssessment.course_title}
                        onChange={(e) => setNewAssessment({ ...newAssessment, course_title: e.target.value })}
                        required={newAssessment.assessment_type === 'Training Course'}
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="scheduled_date">Scheduled Date & Time *</Label>
                    <Input
                      id="scheduled_date"
                      data-testid="scheduled-date-input"
                      type="datetime-local"
                      value={newAssessment.scheduled_date}
                      onChange={(e) => setNewAssessment({ ...newAssessment, scheduled_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="assessor">Assessor</Label>
                    <Input
                      id="assessor"
                      data-testid="assessor-input"
                      value={newAssessment.assessor}
                      onChange={(e) => setNewAssessment({ ...newAssessment, assessor: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      data-testid="assessment-notes-input"
                      value={newAssessment.notes}
                      onChange={(e) => setNewAssessment({ ...newAssessment, notes: e.target.value })}
                    />
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-assessment-button">
                    Schedule Assessment
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </CardHeader>
          <CardContent>
            {filteredAssessments.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400 text-sm">No assessments for this filter</p>
            ) : (
              <div className="space-y-3">
                {filteredAssessments.map((assessment) => (
                  <div key={assessment.id} className="p-4 border dark:border-slate-700 rounded-lg" data-testid={`assessment-${assessment.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-slate-800 dark:text-slate-200">{assessment.assessment_type}</p>
                        {assessment.course_title && (
                          <p className="text-sm text-slate-600 dark:text-slate-400">{assessment.course_title}</p>
                        )}
                      </div>
                      <Badge
                        variant={assessment.status === 'completed' ? 'default' : assessment.status === 'overdue' ? 'destructive' : 'secondary'}
                        className={assessment.status === 'due' ? 'bg-amber-100 text-amber-800 border-amber-300' : ''}
                        data-testid={`assessment-status-${assessment.id}`}
                      >
                        {assessment.status === 'overdue' ? 'due' : assessment.status}
                      </Badge>
                    </div>
                    <div className="space-y-1 text-sm text-slate-600 dark:text-slate-400">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        Scheduled: {format(new Date(assessment.scheduled_date), 'MMM d, yyyy HH:mm')}
                      </div>
                      {assessment.status === 'completed' && assessment.completed_date && (
                        <div className="flex items-center">
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Completed: {format(new Date(assessment.completed_date), 'MMM d, yyyy')}
                        </div>
                      )}
                      {assessment.result && (
                        <div className={`px-2 py-1 rounded mt-1 ${
                          assessment.result === 'passed' 
                            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 border dark:border-slate-700 border-green-200' 
                            : 'bg-red-50 dark:bg-red-900/20 text-red-700 border dark:border-slate-700 border-red-200'
                        }`}>
                          {assessment.result === 'passed' ? '✓ Passed' : '✗ Failed'}
                        </div>
                      )}
                      {assessment.completion_notes && (
                        <div className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded mt-1">
                          <p className="text-xs font-medium text-slate-700 dark:text-slate-300">Comments:</p>
                          <p className="text-xs text-slate-600 dark:text-slate-400">{assessment.completion_notes}</p>
                        </div>
                      )}
                      {assessment.assessor && (
                        <div>
                          {assessment.assessment_type === 'Training Course' ? 'Trainer' : 
                           assessment.assessment_type === 'Coaching Intervention' ? 'Coach' :
                           assessment.assessment_type === 'Coaching Ride Along' ? 'Coach' : 'Assessor'}: {assessment.assessor}
                        </div>
                      )}
                      {assessment.last_p2p_completion && (
                        <div className="text-amber-700 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded mt-1">
                          📌 Last P2P: {format(new Date(assessment.last_p2p_completion), 'MMM d, yyyy')}
                          <span className="ml-2 font-medium">
                            (Gap: {Math.floor((new Date(assessment.scheduled_date) - new Date(assessment.last_p2p_completion)) / (1000 * 60 * 60 * 24))} days)
                          </span>
                        </div>
                      )}
                      {!assessment.last_p2p_completion && assessment.assessment_type === 'Yearly Peer-to-Peer Review' && assessment.status === 'overdue' && (
                        <div className="text-red-600 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded mt-1">
                          ⚠️ First P2P assessment - no previous completion
                        </div>
                      )}
                    </div>
                    {assessment.notes && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">{assessment.notes}</p>
                    )}
                    
                    {currentUser && (
                      <AssessmentFiles 
                        assessment={assessment} 
                        onUpdate={fetchDriverData}
                        canManage={canManageAssessments}
                        currentUserRole={currentUser.role}
                      />
                    )}
                    
                    <div className="flex space-x-2 mt-3">
                      {canManageAssessments && (assessment.status === 'scheduled' || assessment.status === 'overdue' || assessment.status === 'due') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleOpenCompleteAssessment(assessment)}
                          data-testid={`complete-assessment-${assessment.id}`}
                        >
                          Mark as Completed
                        </Button>
                      )}
                      {canManageAssessments && (assessment.status === 'scheduled' || assessment.status === 'overdue' || assessment.status === 'due') && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenReschedule(assessment)}
                            data-testid={`reschedule-assessment-${assessment.id}`}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Reschedule
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteAssessment(assessment.id)}
                            data-testid={`delete-assessment-${assessment.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {canManageAssessments && assessment.status === 'completed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenEditCompleted(assessment)}
                          data-testid={`edit-completed-assessment-${assessment.id}`}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit Notes
                        </Button>
                      )}
                      {currentUser?.role === 'admin' && assessment.status === 'completed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteAssessment(assessment.id)}
                          data-testid={`delete-completed-assessment-${assessment.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>


      {/* Reschedule Dialog */}
      <Dialog open={isRescheduleDialogOpen} onOpenChange={setIsRescheduleDialogOpen}>
        <DialogContent data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule Assessment</DialogTitle>
            <DialogDescription>
              Change the scheduled date for this assessment.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRescheduleAssessment} className="space-y-4">
            {selectedAssessment && (
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="font-medium text-slate-800 dark:text-slate-200">{selectedAssessment.assessment_type}</p>
                {selectedAssessment.course_title && (
                  <p className="text-sm text-slate-600 dark:text-slate-400">{selectedAssessment.course_title}</p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="reschedule-date">New Scheduled Date *</Label>
              <Input
                id="reschedule-date"
                data-testid="reschedule-date-input"
                type="date"
                value={rescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                required
              />
            </div>
            <div className="flex space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setIsRescheduleDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="confirm-reschedule-button">
                Reschedule
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Driver Dialog */}
      <Dialog open={isEditDriverDialogOpen} onOpenChange={setIsEditDriverDialogOpen}>
        <DialogContent data-testid="edit-driver-dialog">
          <DialogHeader>
            <DialogTitle>Edit Driver Information</DialogTitle>
            <DialogDescription>
              Update driver details including status (active/inactive for casual or temporary drivers).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditDriver} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                data-testid="edit-driver-name-input"
                value={editDriverData.name}
                onChange={(e) => setEditDriverData({ ...editDriverData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-employee-id">Employee ID</Label>
              <Input
                id="edit-employee-id"
                data-testid="edit-driver-employee-id-input"
                value={editDriverData.employee_id}
                onChange={(e) => setEditDriverData({ ...editDriverData, employee_id: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-hire-date">Hire Date</Label>
              <Input
                id="edit-hire-date"
                data-testid="edit-driver-hire-date-input"
                type="date"
                value={editDriverData.hire_date}
                onChange={(e) => setEditDriverData({ ...editDriverData, hire_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-status">Status *</Label>
              <select
                id="edit-status"
                data-testid="edit-driver-status-select"
                className="w-full px-3 py-2 border dark:border-slate-700 rounded-md"
                value={editDriverData.status}
                onChange={(e) => setEditDriverData({ ...editDriverData, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive - Casual/Temporary</option>
              </select>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-shift">Shift</Label>
                <select
                  id="edit-shift"
                  data-testid="edit-driver-shift-select"
                  className="w-full px-3 py-2 border dark:border-slate-700 rounded-md"
                  value={editDriverData.shift}
                  onChange={(e) => setEditDriverData({ ...editDriverData, shift: e.target.value })}
                >
                  <option value="">Not set</option>
                  <option value="Early">Early</option>
                  <option value="Late">Late</option>
                  <option value="Night">Night</option>
                  <option value="Part-Time">Part-Time</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-employment-type">Employment Type</Label>
                <select
                  id="edit-employment-type"
                  data-testid="edit-driver-employment-type-select"
                  className="w-full px-3 py-2 border dark:border-slate-700 rounded-md"
                  value={editDriverData.employment_type}
                  onChange={(e) => setEditDriverData({ ...editDriverData, employment_type: e.target.value })}
                >
                  <option value="">Not set</option>
                  <option value="Permanent">Permanent</option>
                  <option value="Agency">Agency</option>
                </select>
              </div>
            </div>

              <p className="text-xs text-slate-500 dark:text-slate-400">
                Set to inactive for casual or agency drivers who are temporarily unavailable
              </p>
            </div>
            <div className="flex space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setIsEditDriverDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="submit-edit-driver-button">
                Update Driver
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Complete Assessment Dialog */}
      <Dialog open={isCompleteDialogOpen} onOpenChange={setIsCompleteDialogOpen}>
        <DialogContent data-testid="complete-assessment-dialog">
          <DialogHeader>
            <DialogTitle>Complete Assessment</DialogTitle>
            <DialogDescription>
              Select the date when this assessment was completed.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCompleteAssessment} className="space-y-4">
            {selectedAssessment && (
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="font-medium text-slate-800 dark:text-slate-200">{selectedAssessment.assessment_type}</p>
                {selectedAssessment.course_title && (
                  <p className="text-sm text-slate-600 dark:text-slate-400">{selectedAssessment.course_title}</p>
                )}
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Scheduled: {format(new Date(selectedAssessment.scheduled_date), 'MMM d, yyyy')}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="completion-date">Completion Date *</Label>
              <Input
                id="completion-date"
                data-testid="completion-date-input"
                type="date"
                value={completionDate}
                onChange={(e) => setCompletionDate(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="result">Result *</Label>
              <select
                id="result"
                data-testid="completion-result-select"
                className="w-full px-3 py-2 border dark:border-slate-700 rounded-md"
                value={completionResult}
                onChange={(e) => setCompletionResult(e.target.value)}
              >
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="completion-notes">Comments</Label>
              <Textarea
                id="completion-notes"
                data-testid="completion-notes-input"
                placeholder="Add any comments about this assessment (required if failed)"
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                rows={3}
                required={completionResult === 'failed'}
              />
              {completionResult === 'failed' && (
                <p className="text-xs text-red-600">
                  Comments are required when marking an assessment as failed
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="pdf-upload">Attach PDFs (Optional)</Label>
              <Input
                id="pdf-upload"
                data-testid="pdf-upload-input"
                type="file"
                accept=".pdf"
                multiple
                onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                You can attach multiple PDF files. Additional files can be added after completion.
              </p>
              {selectedFiles.length > 0 && (
                <div className="text-xs text-slate-600 dark:text-slate-400">
                  {selectedFiles.length} file(s) selected: {selectedFiles.map(f => f.name).join(', ')}
                </div>
              )}
            </div>
            <div className="flex space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setIsCompleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="confirm-complete-button">
                Mark as {completionResult === 'passed' ? 'Passed' : 'Failed'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Completed Assessment Dialog */}
      <Dialog open={isEditCompletedDialogOpen} onOpenChange={setIsEditCompletedDialogOpen}>
        <DialogContent data-testid="edit-completed-dialog">
          <DialogHeader>
            <DialogTitle>Edit Completed Assessment</DialogTitle>
            <DialogDescription>
              Update the result and comments for this completed assessment.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditCompleted} className="space-y-4">
            {selectedAssessment && (
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="font-medium text-slate-800 dark:text-slate-200">{selectedAssessment.assessment_type}</p>
                {selectedAssessment.course_title && (
                  <p className="text-sm text-slate-600 dark:text-slate-400">{selectedAssessment.course_title}</p>
                )}
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Completed: {selectedAssessment.completed_date && format(new Date(selectedAssessment.completed_date), 'MMM d, yyyy')}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="edit-result">Result *</Label>
              <select
                id="edit-result"
                data-testid="edit-completion-result-select"
                className="w-full px-3 py-2 border dark:border-slate-700 rounded-md"
                value={completionResult}
                onChange={(e) => setCompletionResult(e.target.value)}
              >
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-completion-notes">Comments</Label>
              <Textarea
                id="edit-completion-notes"
                data-testid="edit-completion-notes-input"
                placeholder="Add or update comments about this assessment"
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                rows={4}
                required={completionResult === 'failed'}
              />
              {completionResult === 'failed' && (
                <p className="text-xs text-red-600">
                  Comments are required for failed assessments
                </p>
              )}
            </div>
            <div className="flex space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setIsEditCompletedDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="submit-edit-completed-button">
                Update Assessment
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Accident Dialog */}
      <Dialog open={isEditAccidentDialogOpen} onOpenChange={setIsEditAccidentDialogOpen}>
        <DialogContent data-testid="edit-accident-dialog">
          <DialogHeader>
            <DialogTitle>Edit Accident Record</DialogTitle>
            <DialogDescription>
              Update accident details including date, description, and severity.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditAccident} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-accident-date">Date & Time *</Label>
              <Input
                id="edit-accident-date"
                data-testid="edit-accident-date-input"
                type="datetime-local"
                value={editAccidentData.date}
                onChange={(e) => setEditAccidentData({ ...editAccidentData, date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-severity">Severity</Label>
              <select
                id="edit-severity"
                data-testid="edit-accident-severity-select"
                className="w-full px-3 py-2 border dark:border-slate-700 rounded-md"
                value={editAccidentData.severity}
                onChange={(e) => setEditAccidentData({ ...editAccidentData, severity: e.target.value })}
              >
                <option value="minor">Minor</option>
                <option value="moderate">Moderate</option>
                <option value="severe">Severe</option>
              </select>
            </div>
            {currentUser?.role === 'admin' && (
              <div className="space-y-2">
                <Label htmlFor="edit-blameworthy">Blameworthy Decision (Admin Only)</Label>
                <select
                  id="edit-blameworthy"
                  data-testid="edit-accident-blameworthy-select"
                  className="w-full px-3 py-2 border dark:border-slate-700 rounded-md"
                  value={editAccidentData.blameworthy}
                  onChange={(e) => setEditAccidentData({ ...editAccidentData, blameworthy: e.target.value })}
                >
                  <option value="">Not Set</option>
                  <option value="non-blameworthy">Non-Blameworthy</option>
                  <option value="blameworthy">Blameworthy</option>
                </select>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  This decision is normally set via VCR completion, but can be added manually
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description *</Label>
              <Textarea
                id="edit-description"
                data-testid="edit-accident-description-input"
                value={editAccidentData.description}
                onChange={(e) => setEditAccidentData({ ...editAccidentData, description: e.target.value })}
                required
              />
            </div>
            <div className="flex space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setIsEditAccidentDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="submit-edit-accident-button">
                Update Record
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}